/**
 * WebAuthn glue for member passkeys.
 *
 * Turns the server's JSON into the options `navigator.credentials` takes, and
 * the browser's answer back into the fields the server reads. Every binary
 * value on the wire is base64url without padding, which is what the server
 * writes with `Base.url_encode64(..., padding: false)`.
 *
 * Everything except the two `navigator.credentials` wrappers is pure, so it
 * runs under vitest's node environment.
 *
 * `userVerification: "required"` is fixed here rather than read from the
 * server: the server requires the UV flag on every assertion and attestation,
 * but does not send a `userVerification` field to echo.
 */
import UAParser from "ua-parser-js";

/** `publicKey` of GET /members/step-up/challenges/:id. */
export type ServerAssertionOptions = {
  challenge: string;
  rpId: string;
  allowCredentialIds: string[];
  timeoutMs: number;
};

/** `publicKey` of POST /members/passkeys/registration. */
export type ServerRegistrationOptions = {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  excludeCredentialIds: string[];
  timeoutMs: number;
};

/** The fields POST /members/step-up/challenges/:id/verify reads. */
export type AssertionFields = {
  credentialId: string;
  authenticatorData: string;
  signature: string;
  clientDataJson: string;
};

/** The browser half of POST /members/passkeys. */
export type AttestationFields = {
  attestationObject: string;
  clientDataJson: string;
};

// ES256 first: it is what platform authenticators and security keys make by
// default. RS256 covers Windows Hello on older builds.
export const PUB_KEY_CRED_PARAMS: PublicKeyCredentialParameters[] = [
  { type: "public-key", alg: -7 },
  { type: "public-key", alg: -257 },
];

const toBytes = (input: ArrayBuffer | ArrayBufferView): Uint8Array =>
  input instanceof ArrayBuffer
    ? new Uint8Array(input)
    : new Uint8Array(input.buffer, input.byteOffset, input.byteLength);

export const bufferToBase64url = (
  input: ArrayBuffer | ArrayBufferView,
): string => {
  const bytes = toBytes(input);
  // A loop, not `String.fromCharCode(...bytes)`: spreading a large
  // attestation object can overflow the call stack.
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const base64urlToBuffer = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};

const credentialDescriptors = (
  ids: string[],
): PublicKeyCredentialDescriptor[] =>
  ids.map((id) => ({ type: "public-key", id: base64urlToBuffer(id) }));

/**
 * Options for `navigator.credentials.get`. `timeoutMs` defaults to the
 * server's, which is the time left when the challenge was read; the page
 * passes the time left now, so a prompt never outlives the challenge.
 */
export const toRequestOptions = (
  options: ServerAssertionOptions,
  timeoutMs: number = options.timeoutMs,
): PublicKeyCredentialRequestOptions => ({
  challenge: base64urlToBuffer(options.challenge),
  rpId: options.rpId,
  allowCredentials: credentialDescriptors(options.allowCredentialIds),
  userVerification: "required",
  timeout: timeoutMs,
});

/**
 * Options for `navigator.credentials.create`. `timeoutMs` defaults to the
 * server's, which is the token's whole life at mint; a caller using options
 * fetched earlier passes what is left, so the prompt never outlives the token.
 */
export const toCreationOptions = (
  options: ServerRegistrationOptions,
  timeoutMs: number = options.timeoutMs,
): PublicKeyCredentialCreationOptions => ({
  challenge: base64urlToBuffer(options.challenge),
  rp: { id: options.rp.id, name: options.rp.name },
  user: {
    id: base64urlToBuffer(options.user.id),
    name: options.user.name,
    displayName: options.user.displayName,
  },
  pubKeyCredParams: PUB_KEY_CRED_PARAMS,
  excludeCredentials: credentialDescriptors(options.excludeCredentialIds),
  authenticatorSelection: {
    residentKey: "preferred",
    userVerification: "required",
  },
  timeout: timeoutMs,
  attestation: "none",
});

/** The parts of an assertion credential the server needs. */
export type AssertionCredential = {
  rawId: ArrayBuffer;
  response: {
    authenticatorData: ArrayBuffer;
    signature: ArrayBuffer;
    clientDataJSON: ArrayBuffer;
  };
};

/** The parts of a registration credential the server needs. */
export type AttestationCredential = {
  response: { attestationObject: ArrayBuffer; clientDataJSON: ArrayBuffer };
};

export const serialiseAssertion = (
  credential: AssertionCredential,
): AssertionFields => ({
  credentialId: bufferToBase64url(credential.rawId),
  authenticatorData: bufferToBase64url(credential.response.authenticatorData),
  signature: bufferToBase64url(credential.response.signature),
  clientDataJson: bufferToBase64url(credential.response.clientDataJSON),
});

export const serialiseAttestation = (
  credential: AttestationCredential,
): AttestationFields => ({
  attestationObject: bufferToBase64url(credential.response.attestationObject),
  clientDataJson: bufferToBase64url(credential.response.clientDataJSON),
});

/**
 * What went wrong in a browser ceremony, in the terms the page acts on.
 *
 * - `cancelled`: the ceremony was aborted (AbortError). Nothing to say.
 * - `not_allowed`: NotAllowedError. Browsers use it, deliberately without
 *   telling which, for a closed prompt, a timeout, no passkey for this account
 *   on this device, and (in Safari) a prompt started without a recent click.
 *   It is never silent: the page says the prompt closed and offers a way on.
 * - `already_registered`: `create` matched an excluded credential, so this
 *   authenticator already holds a passkey for this account.
 * - `failed`: anything else (wrong origin for the RP ID, no support, a bug).
 */
export type CeremonyProblem =
  | "cancelled"
  | "not_allowed"
  | "already_registered"
  | "failed";

/**
 * The problem behind anything a ceremony threw: a CeremonyError from the
 * wrappers below says its own, a browser DOMException is read by name.
 */
export const classifyCeremonyError = (error: unknown): CeremonyProblem => {
  if (error instanceof CeremonyError) return error.problem;
  const name =
    error && typeof error === "object"
      ? (error as { name?: unknown }).name
      : undefined;
  if (name === "AbortError") return "cancelled";
  if (name === "NotAllowedError") return "not_allowed";
  if (name === "InvalidStateError") return "already_registered";
  return "failed";
};

/** Thrown by the wrappers below when the browser declines the ceremony. */
export class CeremonyError extends Error {
  readonly problem: CeremonyProblem;

  constructor(problem: CeremonyProblem, cause?: unknown) {
    super(problem);
    this.name = "CeremonyError";
    this.problem = problem;
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

/** Whether this browser can run a passkey ceremony at all. */
export const webAuthnAvailable = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.PublicKeyCredential === "function" &&
  typeof navigator !== "undefined" &&
  Boolean(navigator.credentials);

/**
 * Asks the browser to sign; throws a CeremonyError if it does not. `signal`
 * closes the prompt from the page (it then rejects as `cancelled`).
 */
export const getAssertion = async (
  publicKey: PublicKeyCredentialRequestOptions,
  signal?: AbortSignal,
): Promise<AssertionFields> => {
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.get({ publicKey, signal });
  } catch (error) {
    throw new CeremonyError(classifyCeremonyError(error), error);
  }
  if (!credential) throw new CeremonyError("not_allowed");
  return serialiseAssertion(credential as unknown as AssertionCredential);
};

/**
 * Asks the browser to make a passkey; throws a CeremonyError if it does not.
 * `navigator.credentials.create` is called before this function first awaits,
 * so a caller that calls it straight from a click keeps the click's activation.
 */
export const createAttestation = async (
  publicKey: PublicKeyCredentialCreationOptions,
): Promise<AttestationFields> => {
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.create({ publicKey });
  } catch (error) {
    throw new CeremonyError(classifyCeremonyError(error), error);
  }
  if (!credential) throw new CeremonyError("not_allowed");
  return serialiseAttestation(credential as unknown as AttestationCredential);
};

/**
 * A starting label for a new passkey, from the browser it is made in:
 * "Passkey on macOS (Chrome)". The person can change it before saving.
 */
export const defaultPasskeyLabel = (userAgent: string): string => {
  const { browser, os } = new UAParser(userAgent).getResult();
  if (os.name && browser.name) return `Passkey on ${os.name} (${browser.name})`;
  if (os.name) return `Passkey on ${os.name}`;
  if (browser.name) return `Passkey on ${browser.name}`;
  return "Passkey";
};

/** The longest label the portal sends. Long enough for anything a person types. */
export const PASSKEY_LABEL_MAX = 64;

/** A label as it will be saved: trimmed, never empty, never over the limit. */
export const normalisePasskeyLabel = (raw: string, fallback: string): string => {
  const trimmed = raw.trim().slice(0, PASSKEY_LABEL_MAX).trim();
  return trimmed || fallback;
};
